/**
 * Update User API
 *
 * API functions for updating user profile data
 */

import { buildApiUrl, handleApiResponse } from '@/src/shared/api';
import { fetchWithTokenRefresh } from '@/src/shared/api/refreshToken';

export interface UpdateUserProfileDto {
  name?: string;
  phone?: string;
}

export interface UpdateActorDto {
  name?: string;
  phone?: string;
  displayName?: string;
}

export interface Actor {
  id: string;
  type: string;
  name: string;
  phone: string | null;
  displayName: string | null;
  avatarUrl: string | null;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface User {
  id: string;
  email: string;
  name: string;
  phone: string | null;
  avatarUrl: string | null;
  roles: string[];
  isEmailVerified: boolean;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Update user profile (name, phone)
 */
export async function updateUserProfile(
  dto: UpdateUserProfileDto
): Promise<User> {
  const url = buildApiUrl('/users/me');

  // Filter out undefined values to avoid sending empty fields
  const filteredDto = Object.fromEntries(
    Object.entries(dto).filter(([, value]) => value !== undefined)
  );

  // Don't make request if there's nothing to update - return mock response
  if (Object.keys(filteredDto).length === 0) {
    console.log('[updateUserProfile] No fields to update, skipping API call');
    // Return a mock response to avoid errors
    return {} as User;
  }

  console.log('[updateUserProfile] Sending:', filteredDto);

  const response = await fetchWithTokenRefresh(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include', // Include cookies for auth
    body: JSON.stringify(filteredDto),
  });

  return handleApiResponse<User>(response);
}

/**
 * Upload user avatar
 */
export async function uploadAvatar(file: File): Promise<User> {
  const url = buildApiUrl('/users/me/avatar');

  const formData = new FormData();
  formData.append('file', file);

  const response = await fetchWithTokenRefresh(url, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });

  return handleApiResponse<User>(response);
}

/**
 * Update actor profile (name, phone, displayName)
 * Actor/Persona Architecture: Profile data is stored in Actor model
 */
export async function updateActorProfile(dto: UpdateActorDto): Promise<Actor> {
  const url = buildApiUrl('/users/me/actor');

  console.log('[updateActorProfile] Input DTO:', dto);

  // Filter out undefined values to avoid sending empty fields
  const filteredDto = Object.fromEntries(
    Object.entries(dto).filter(([, value]) => value !== undefined && value !== '')
  );

  // Don't make request if there's nothing to update - return mock response
  if (Object.keys(filteredDto).length === 0) {
    console.log('[updateActorProfile] No fields to update, skipping API call');
    return {} as Actor;
  }

  console.log('[updateActorProfile] Sending to', url, ':', filteredDto);

  const response = await fetchWithTokenRefresh(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(filteredDto),
  });

  const result = await handleApiResponse<Actor>(response);

  console.log('[updateActorProfile] Response:', result);

  return result;
}
